import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from 'react';

type PaginatedQuery<T> = {
  data?: { results: T[]; totalCount?: number };
  isLoading: boolean;
  isFetching: boolean;
};

export function useLoadMoreAccumulator<T, K>(
  resetKey: K,
  query: PaginatedQuery<T>,
  page: number,
  setPage: Dispatch<SetStateAction<number>>,
) {
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const prevKey = useRef(resetKey);

  useEffect(() => {
    if (prevKey.current !== resetKey) {
      setItems([]);
      setTotal(0);
      setPage(0);
      prevKey.current = resetKey;
    }
  }, [resetKey, setPage]);

  useEffect(() => {
    const results = query.data?.results;
    if (!results) return;
    if (page === 0) {
      setItems(results);
      setTotal(query.data?.totalCount ?? 0);
    } else {
      setItems((prev) => [...prev, ...results]);
    }
  }, [query.data, page]);

  return {
    items,
    total,
    hasMore: items.length < total,
    isLoading: query.isLoading && page === 0,
    isLoadingMore: page > 0 && query.isFetching,
    onLoadMore: () => setPage((p) => p + 1),
  };
}
